// The t node type is used to wrap bare text nodes. Do not delete it.
const CONTENT_SELECTOR = 'a, abbr, address, b, bdi, bdo, blockquote, button, caption, cite, code, data, dd, del, details, dfn, div, dt, em, figcaption, h1, h2, h3, h4, h5, h6, i, ins, input[type="button"], input[type="reset"], input[type="submit"], label, legend, li, mark, optgroup, option, output, p, pre, q, s, small, span, strong, sub, summary, sup, t, td, textarea, th, time, u';
const TRANSLATABLE_ATTRIBUTES = ['alt', 'aria-label', 'placeholder', 'title'];

class DeepLTranslator {
  constructor() {
    this.debug = false;

    this.originalTextMap = new Map();
    this.attributeTextMap = new Map();
    this.variablesTextMap = new Map();
    this.originalVariables = new Map();
    this.targetLang = null;
    this.abortController = null;
    this.selectElements = [];

    // Define which window variables and their fields should be translated
    // Key: window variable name, Value: array of field names to translate
    this.translatableVariables = {
      'wp_variables': ['search_labels', 'sr_only_label'],
      'nlds_plugin_variables': ['dfc', 'pagination', 'events']
    };

    // Bind event handlers once during construction for better performance
    this.handleContentUpdated = this.handleContentUpdated.bind( this );
    this.handleSelectChange = this.handleSelectChange.bind( this );

    // on event denhaag.wizard.change, trigger nlds:content-updated
    document.addEventListener( 'denhaag.wizard.change', ( e ) => {
      const contentUpdatedEvent = new CustomEvent( 'nlds:content-updated', { detail: { element: null } } );
      document.dispatchEvent( contentUpdatedEvent );
    } );
  }

  /**
   * Log messages if debug is enabled.
   * @param args
   */
  log(...args) {
    if (this.debug) {
      console.log('[DeepL]', ...args);
    }
  }

  /**
   * Check if a string is a URL.
   * @param {string} str The string to check.
   * @return {boolean} True if the string is a URL.
   */
  isUrl(str) {
    // Check if string starts with http:// or https:// or contains ://
    return /^https?:\/\//.test(str) || /:\/\//.test(str);
  }

  /**
   * Wraps bare text nodes in <t> so translation won't remove SVGs or icons.
   * Detects and preserves leading/trailing spaces outside the <t> wrapper.
   * @param {HTMLElement} root The root element to process.
   */
  wrapBareTextNodes(root = document.body) {
    const treeWalker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_ELEMENT,
      null
    );

    while (treeWalker.nextNode()) {
      const el = treeWalker.currentNode;

      // Skip elements we don't translate anyway
      if (!el.matches( CONTENT_SELECTOR )) continue;

      // Skip admin bar
      if (el.closest( '#wpadminbar' )) continue;

      // Get child text nodes that are not empty
      const textNodes = Array.from( el.childNodes ).filter(
        (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim().length > 0
      );

      textNodes?.forEach( (textNode) => {
        // Skip if already wrapped
        if (textNode.parentElement?.tagName === "T") return;

        // No need to wrap if the text node is the only child
        if (el.childNodes.length === 1) return;

        const originalText = textNode.textContent;
        const trimmedText = originalText.trim();

        // Detect leading and trailing whitespace (use regex to catch all whitespace types: \n, \t, \xa0, etc.)
        const hasLeadingSpace = originalText !== trimmedText && /^\s/.test( originalText );
        const hasTrailingSpace = originalText !== trimmedText && /\s$/.test( originalText );

        // Create wrapper and set trimmed text
        const wrapper = document.createElement( "t" );
        wrapper.textContent = trimmedText;

        // Insert the wrapper
        textNode.parentNode.insertBefore( wrapper, textNode );

        // Add trailing space after wrapper if needed
        if (hasTrailingSpace) {
          const trailingSpaceNode = document.createTextNode( ' ' );
          textNode.parentNode.insertBefore( trailingSpaceNode, textNode );
        }

        // Add leading space before wrapper if needed
        if (hasLeadingSpace) {
          const leadingSpaceNode = document.createTextNode( ' ' );
          textNode.parentNode.insertBefore( leadingSpaceNode, wrapper );
        }

        // Remove the original text node
        textNode.parentNode.removeChild( textNode );
      } );
    }
  }

  /**
   * Insert styles for the <t> wrapper element.
   */
  insertStyles() {
    const style = document.createElement( 'style' );
    style.textContent = `
    t {
      pointer-events: none;
      background: transparent;
      color: inherit;
    }
    `;
    document.head.appendChild( style );
  }

  /**
   * Recursively process an object and extract all translatable strings.
   * @param {Object} obj The object to process.
   * @param {string} windowVarName The name of the window variable.
   * @param {Array} path The path to the current object (array of keys).
   * @return {boolean} True if new text was found.
   */
  processObjectRecursively(obj, windowVarName, path = []) {
    let hasNewText = false;

    Object.keys( obj )?.forEach( (key) => {
      const value = obj[key];
      const currentPath = [...path, key];

      // Skip if key contains 'nonce' or rest endpoints
      if (key.toLowerCase().includes('nonce') || key.toLowerCase().includes('rest')) {
        return;
      }

      // Handle nested objects recursively
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const nestedHasNewText = this.processObjectRecursively(value, windowVarName, currentPath);
        hasNewText = hasNewText || nestedHasNewText;
      }
      // Handle string values.
      else if (typeof value === 'string' && value.trim().length > 0) {
        // Skip if value is a URL
        if (this.isUrl(value)) {
          return;
        }

        const normalizedText = value.trim().replace(/\s\s+/g, " ");

        if (!this.variablesTextMap.get(normalizedText)) {
          this.variablesTextMap.set(normalizedText, [{
            windowVarName,
            path: currentPath
          }]);
          hasNewText = true;
        } else {
          const existingVars = this.variablesTextMap.get(normalizedText);
          if (existingVars) {
            const alreadyExists = existingVars.some(
              item => item.windowVarName === windowVarName &&
                      JSON.stringify(item.path) === JSON.stringify(currentPath)
            );
            if (!alreadyExists) {
              existingVars.push({windowVarName, path: currentPath});
            }
          } else {
            this.variablesTextMap.set(normalizedText, [{
              windowVarName,
              path: currentPath
            }]);
          }
        }
      }
    });

    return hasNewText;
  }

  /**
   * Save and process JavaScript variables for translation.
   * @return {boolean} True if new text was found.
   */
  saveJavaScriptVariables() {
    let hasNewText = false;

    // Loop through configured translatable variables
    Object.keys(this.translatableVariables).forEach((windowVarName) => {
      const windowVar = window[windowVarName];

      if (!windowVar) return;

      // Store original variable (deep clone) if not already stored.
      if (!this.originalVariables.has( windowVarName )) {
        this.originalVariables.set( windowVarName, JSON.parse( JSON.stringify( windowVar ) ) );
      }

      const fieldsToTranslate = this.translatableVariables[windowVarName];

      fieldsToTranslate?.forEach((fieldName) => {
        const fieldValue = windowVar[fieldName];

        if (!fieldValue) return;

        // Handle objects - translate all string properties recursively.
        if (typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
          const nestedHasNewText = this.processObjectRecursively(fieldValue, windowVarName, [fieldName]);
          hasNewText = hasNewText || nestedHasNewText;
        }
        // Handle direct strings.
        else if (typeof fieldValue === 'string' && fieldValue.trim().length > 0) {
          // Skip if fieldName contains 'nonce' or value is a URL
          if (fieldName.toLowerCase().includes( 'nonce' ) || this.isUrl( fieldValue )) {
            // Process skipped.
            return;
          }

          const normalizedText = fieldValue.trim().replace(/\s\s+/g, " ");

          if (!this.variablesTextMap.get(normalizedText)) {
            this.variablesTextMap.set(normalizedText, [{
              windowVarName,
              path: [fieldName]
            }]);
            hasNewText = true;
          } else {
            const existingVars = this.variablesTextMap.get(normalizedText);
            if (existingVars) {
              const alreadyExists = existingVars.some(
                item => item.windowVarName === windowVarName &&
                        JSON.stringify(item.path) === JSON.stringify([fieldName])
              );
              if (!alreadyExists) {
                existingVars.push({windowVarName, path: [fieldName]});
              }
            }
          }
        }
      });
    });

    return hasNewText;
  }

  /**
   * Save original text for translation.
   * @param {HTMLElement} root The root element to process.
   * @return {boolean} True if new text was found.
   */
  saveOriginalText(root = document.body) {
    this.wrapBareTextNodes( root );

    // Get elements for text content translation
    const contentElements = root === document.body
      ? document.querySelectorAll( CONTENT_SELECTOR )
      : root.querySelectorAll( CONTENT_SELECTOR );

    // Get elements with translatable attributes
    const attributeSelector = TRANSLATABLE_ATTRIBUTES.map(attr => `[${attr}]`).join(', ');
    const attributeElements = root === document.body
      ? document.querySelectorAll( attributeSelector )
      : root.querySelectorAll( attributeSelector );

    // Combine both sets of elements (use Set to avoid duplicates)
    const allElements = new Set([...contentElements, ...attributeElements]);

    if (allElements.size === 0) {
      return false;
    }

    let hasNewText = false;

    allElements.forEach( (el) => {
      if (el instanceof HTMLElement) {
        // Skip admin bar
        if (el.closest( '#wpadminbar' )) return;

        // Process text content
        const directText = Array.from( el.childNodes )
          .filter(
            (node) =>
              node.nodeType === Node.TEXT_NODE ||
              (node.nodeType === Node.ELEMENT_NODE && node.nodeName === "BR")
          )
          .map( (node) => node.textContent?.trim() )
          .join( " " )
          .trim();

        if (directText && /\p{L}/u.test( directText )) {
          const normalizedText = directText.replace( /\s\s+/g, " " ); // Normalize whitespace
          if (!this.originalTextMap.get(normalizedText)) {
            if (el.dataset.deeplTranslated !== 'true') {
              this.originalTextMap.set( normalizedText, [el] );
              hasNewText = true;
            }
          } else {
            // If this text has already been processed, add the element to the list, if not already present
            const elementsWithSameText = this.originalTextMap.get( normalizedText );
            if (elementsWithSameText && !elementsWithSameText.includes( el )) {
              elementsWithSameText.push( el );
            }
          }
        }

        // Process translatable attributes
        TRANSLATABLE_ATTRIBUTES.forEach( (attrName) => {
          const attrValue = el.getAttribute( attrName );
          if (attrValue && /\p{L}/u.test( attrValue.trim() )) {
            const normalizedAttrText = attrValue.trim().replace( /\s\s+/g, " " );
            const attrKey = `${normalizedAttrText}`;

            if (!this.originalTextMap.get( attrKey )) {
              if (el.dataset[`deepl${attrName.replace(/-./g, x => x[1].toUpperCase())}Translated`] !== 'true') {
                this.attributeTextMap.set( attrKey, [{element: el, attribute: attrName}] );
                hasNewText = true;
              }
            } else {
              // If this attribute text has already been processed, add the element/attribute pair
              const existingAttrs = this.attributeTextMap.get( attrKey );
              if (existingAttrs) {
                const alreadyExists = existingAttrs.some(
                  item => item.element === el && item.attribute === attrName
                );
                if (!alreadyExists) {
                  existingAttrs.push( {element: el, attribute: attrName} );
                }
              }
            }
          }
        } );
      }
    } );

    return hasNewText;
  }

  /**
   * Handle select change event.
   * @param {Event} event The change event.
   */
  handleSelectChange(event) {
    const changedSelect = event.target;
    const selectedLanguage = changedSelect.value;

    // Sync all other select elements to the same value
    this.selectElements.forEach( select => {
      if (select !== changedSelect) {
        select.value = selectedLanguage;
      }
    });

    // Get the default language from the first option
    const defaultLanguage = NLDSDeepL?.default_language?.iso_alpha2 || changedSelect.options[0]?.value;

    // Get direction from the selected option's data attribute
    const selectedOption = changedSelect.options[changedSelect.selectedIndex];
    const direction = selectedOption?.dataset?.dir || 'ltr';

    if (selectedLanguage === defaultLanguage) {
      window.sessionStorage.removeItem( "DeepLSelectedLanguage" );
      this.revertToOriginalText();
      this.setDirection( direction );
    } else {
      window.sessionStorage.setItem( "DeepLSelectedLanguage", selectedLanguage );
      this.targetLang = selectedLanguage;
      this.translatePage();
    }
  }

  /**
   * Check if an element has been translated.
   * @param {HTMLElement} elToCheck The element to check.
   * @return {boolean} True if the element has been translated, false otherwise.
   */
  isTranslated(elToCheck) {
    return elToCheck.dataset.deeplTranslated === 'true';
  }

  /**
   * Revert all elements to their original text and attributes.
   */
  revertToOriginalText() {
    // Revert text content.
    this.originalTextMap.forEach( (elements, originalText) => elements.forEach( (el) => {
      el.textContent = originalText;
      el.dataset.deeplTranslated = 'false';
    } ) );

    // Revert attributes
    this.attributeTextMap.forEach( (items, originalAttrText) => items.forEach( ({element, attribute}) => {
      element.setAttribute( attribute, originalAttrText );
      element.dataset[`deepl${attribute.replace( /-./g, x => x[1].toUpperCase() )}Translated`] = 'false';
    } ) );

    // Revert JavaScript variables
    if (this.originalVariables.size > 0) {
      this.originalVariables.forEach((originalValue, windowVarName) => {
        if (window[windowVarName]) {
          window[windowVarName] = JSON.parse(JSON.stringify(originalValue));
        }
      });

      // Dispatch event to notify that variables have been reverted
      const event = new CustomEvent('nlds:variables-translated');
      document.dispatchEvent(event);
    }
  }

  /**
   * Translate the entire page.
   */
  async translatePage() {
    // Combine text content, attribute text, and JavaScript variables for translation
    const textContentArray = Array.from( this.originalTextMap.keys() );
    const attributeTextArray = Array.from( this.attributeTextMap.keys() );
    const variablesTextArray = Array.from( this.variablesTextMap.keys() );
    const uniqueTextArray = [...textContentArray, ...attributeTextArray, ...variablesTextArray];

    await this.translateText( uniqueTextArray, this.targetLang );
  }

  /**
   * Translate text array to target language.
   * @param {string[]} textArray Array of text to translate.
   * @param {string} targetLang Target language code.
   */
  async translateText(textArray, targetLang) {
    const {rest_endpoint, rest_nonce} = window.NLDSDeepL;

    // Abort any in-flight request
    if (this.abortController) {
      this.abortController.abort();
    }

    // Create new AbortController for this request
    this.abortController = new AbortController();

    const headers = {
      "Content-Type": "application/json",
      "X-DeepL-Nonce": rest_nonce,
    };

    try {
      const response = await fetch( rest_endpoint, {
        method: "POST",
        headers,
        credentials: "include",
        signal: this.abortController.signal,
        body: JSON.stringify( {
          text: textArray,
          target_lang: targetLang
        } ),
      } );

      if (!response.ok) {
        throw new Error(
          `Request failed with status: ${response.status}`
        );
      }

      const responseData = await response.json();
      document.documentElement.lang = targetLang;
      this.applyTranslations( responseData.data || [] );
      this.setDirection( responseData?.lang?.dir || 'ltr' );
    } catch (error) {
      // Don't log AbortErrors as they're intentional
      if (error.name !== 'AbortError') {
        console.error( error );
      }
    }
  }

  /**
   * Apply translations to elements.
   * @param {Array} translations Array of translation objects.
   */
  applyTranslations(translations) {
    translations?.forEach( (translation) => {
      // Apply text content translations
      const elements = this.originalTextMap.get( translation.text );
      if (elements) {
        elements.forEach( (el) => {
          el.textContent = translation?.translation;
          el.dataset.deeplTranslated = 'true';
        } );
      }

      // Apply attribute translations
      const attributeItems = this.attributeTextMap.get( translation.text );
      if (attributeItems) {
        attributeItems.forEach( ({element, attribute}) => {
          element.setAttribute( attribute, translation?.translation );
          element.dataset[`deepl${attribute.replace(/-./g, x => x[1].toUpperCase())}Translated`] = 'true';
        } );
      }

      // Apply JavaScript variable translations
      const variableItems = this.variablesTextMap.get( translation.text );
      if (variableItems) {
        variableItems.forEach( ({windowVarName, path}) => {
          const windowVar = window[windowVarName];
          if (!windowVar) return;

          // Navigate to the parent object using the path
          let obj = windowVar;
          for (let i = 0; i < path.length - 1; i++) {
            if (obj[path[i]] === undefined) return;
            obj = obj[path[i]];
          }

          // Set the translated value at the final key
          const finalKey = path[path.length - 1];
          if (obj && finalKey) {
            this.log(`Translating ${windowVarName}.${path.join('.')}: "${translation.text}" -> "${translation?.translation}"`);
            obj[finalKey] = translation?.translation;
          }
        } );
      }
    } );

    // Dispatch event to notify that JavaScript variables have been translated
    if (this.variablesTextMap.size > 0) {
      const event = new CustomEvent('nlds:variables-translated');
      document.dispatchEvent(event);
      this.log('Dispatched nlds:variables-translated event');
    }
  }

  /**
   * Set the document direction.
   * @param {'ltr'|'rtl'} direction The direction to set.
   */
  setDirection(direction) {
    document.documentElement.dir = ['ltr', 'rtl'].includes( direction ) ? direction : 'ltr';
    this.log(`Set document direction to ${direction}`);
  }

  /**
   * Initialize the language select elements that should already exist in the DOM.
   * The selects should be rendered server-side for better performance and caching.
   */
  initializeSelect() {
    // Find all select elements with the class (mobile and desktop)
    this.selectElements = Array.from( document.querySelectorAll( '.a11y-toolbar__translate-select' ) );

    if (this.selectElements.length === 0) {
      return;
    }

    // Attach change event listener to all selects
    this.selectElements.forEach( select => {
      select.addEventListener( "change", this.handleSelectChange );

      // Set the selected option based on sessionStorage
      const storedLanguage = window.sessionStorage.getItem( "DeepLSelectedLanguage" );
      if (storedLanguage) {
        select.value = storedLanguage;
      }
    });
  }

  /**
   * Handle content updated events.
   * @param {CustomEvent} e The custom event.
   */
  handleContentUpdated(e) {
    // Only process the updated element if available, otherwise process the entire document.
    const targetElement = e?.detail?.element;
    const hasNewText = targetElement
      ? this.saveOriginalText( targetElement )
      : this.saveOriginalText( document.body );

    // Only translate if there's new text and a target language is set
    if (hasNewText && this.targetLang) {
      this.translatePage();
    }
  }

  /**
   * Initialize the DeepL translator.
   */
  init() {
    // Ensure initialization only happens once.
    const body = document.body;
    if (body.dataset.deeplInitialized === 'true') {
      return;
    }
    body.dataset.deeplInitialized = 'true';

    this.initializeSelect();
    this.insertStyles();
    this.saveOriginalText();
    this.saveJavaScriptVariables();

    // Set the saved language and translate on page load
    const savedLanguage = window.sessionStorage.getItem( "DeepLSelectedLanguage" );

    // Get default language from any select element's first option
    const defaultLanguage = this.selectElements[0]?.options[0]?.value;

    if (savedLanguage && savedLanguage !== defaultLanguage) {
      this.targetLang = savedLanguage;
      this.translatePage();
    }

    // Use the bound handler for better performance
    document.addEventListener( "nlds:content-updated", this.handleContentUpdated, true );
  }
}

// Export factory function for backward compatibility
const deepl = () => new DeepLTranslator();

export default deepl;
